import React from "react";

import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Image,
  Link,
  NoSSR,
  Picture,
} from "@catchoftheday/cg-components";
import { styled, themeGet } from "@catchoftheday/theme";

import { HeaderConfiguration } from "../../types";
import { Container } from "../Container";
import { SearchBar } from "../SearchBar";

import { FlybuysTile } from "./FlybuysTile";
import { MiniCart } from "./MiniCart";
import { MyAccount } from "./MyAccount";
import { WishList } from "./WishList";

// Overriding for web
const LogoContainer = styled(Box)`
  @media (max-width: ${themeGet("breakpoints.lg")}) and (min-width: ${themeGet(
      "breakpoints.md"
    )}) {
    width: 96px;
  }
`;

import { LogoDecoration } from "./LogoDecoration";
import { OnePassFlybuysTile } from "./OnePassFlybuysTile";
import { OnePassTile } from "./OnePassTile";

const TileFlex: React.FC<FlexProps> = (props) => (
  <Flex display={{ xs: "inherit", sm: "none", lg: "inherit" }} {...props} />
);

export type PrimaryHeaderProps = {
  isLoading?: boolean;
  isLoggedIn?: boolean;
  isCCMember?: boolean;
  isFlybuysLinked?: boolean;
  wishItemsAmount?: number;
  cartItemsAmount?: number;
  userName?: string;
  myAccountMessageCount?: number | string;
  headerConfiguration?: HeaderConfiguration;
} & BoxProps;

export const PrimaryHeader: React.FC<PrimaryHeaderProps> = ({
  isLoggedIn,
  isCCMember,
  isFlybuysLinked,
  wishItemsAmount,
  cartItemsAmount,
  userName,
  isLoading,
  myAccountMessageCount,
  headerConfiguration,
  ...props
}) => {
  const primaryHeaderConfig = headerConfiguration?.primaryHeader;
  const eventTheme = headerConfiguration?.headerTheme;
  const defaultWidths = [
    "3.06rem",
    "70px",
    "10.71875rem",
    "8.09rem",
    "10.71875rem",
  ];
  const themedWidths = ["3.0625rem", "70px", "96px", "8.09375rem", "9.625rem"];
  const renderFeatTiles = () => {
    return (
      <>
        {primaryHeaderConfig?.club?.clubCatchTileEnabled != false && (
          <TileFlex data-testid="onepass-header-tile">
            <OnePassTile isLoggedIn={isLoggedIn} />
          </TileFlex>
        )}
        {!isFlybuysLinked && (
          <>
            {isLoggedIn && isCCMember ? (
              <>
                {primaryHeaderConfig?.clubFlybuys?.enabled != false && (
                  <TileFlex>
                    <OnePassFlybuysTile />
                  </TileFlex>
                )}
              </>
            ) : (
              <>
                {primaryHeaderConfig?.flybuys?.enabled != false && (
                  <TileFlex>
                    <FlybuysTile />
                  </TileFlex>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Box as="nav" aria-label="Primary Header Navigation">
      <Container
        backgroundColor="secondary"
        height={{ sm: "70px", md: "95px" }}
        eventTheme={eventTheme}
        position="relative"
        zIndex={2}
        {...props}
      >
        <Flex justifyContent="space-between" height="100%">
          <Flex
            alignItems="center"
            flexGrow={1}
            justifyContent={["center", null, null, "flex-start"]}
            py={0}
          >
            <LogoContainer
              pr={["0.4375rem", "0.875rem", "1.1rem"]}
              width={eventTheme ? themedWidths : defaultWidths}
              position="relative"
            >
              <Link
                href={primaryHeaderConfig?.logo.url || "/"}
                className="js-dnt"
                data-tracking="header-app-logo"
              >
                <Picture data-testid="catch-logo">
                  {eventTheme && <LogoDecoration eventTheme={eventTheme} />}
                  <source
                    media="(max-width: 1024px)"
                    srcSet="https://s.catch.com.au/static/catch/images/logo-condensed-b8974103f2.svg"
                    type="image/svg+xml"
                  />
                  <Image
                    width="300"
                    height="132"
                    alt={primaryHeaderConfig?.logo.alt || "Catch Logo"}
                    src={
                      primaryHeaderConfig?.logo.imageSrc ||
                      "https://s.catch.com.au/static/catch/images/logo-83d9b21199.svg"
                    }
                  />
                </Picture>
              </Link>
            </LogoContainer>
            {primaryHeaderConfig?.searchBar?.enabled != false && (
              <SearchBar
                flexContainerProps={{
                  flexGrow: 1,
                  pr: ["4px", null, "20px"],
                }}
              />
            )}
          </Flex>
          <Flex
            color="white"
            flexBasis={["auto", null, null, null, "660px"]}
            justifyContent="flex-end"
            display={["inherit", "none", "inherit", null, null]}
          >
            {!isLoading && (
              <NoSSR>
                {primaryHeaderConfig?.myAccount?.enabled != false && (
                  <Flex>
                    <MyAccount
                      showFlybuysLink={
                        primaryHeaderConfig?.myAccount?.showFlybuysLink
                      }
                      isLoggedIn={isLoggedIn}
                      isCCMember={isCCMember}
                      isFlybuysLinked={isFlybuysLinked}
                      userName={userName}
                      messageCount={myAccountMessageCount}
                    />
                  </Flex>
                )}
                {renderFeatTiles()}
              </NoSSR>
            )}
            <Flex>
              {primaryHeaderConfig?.wishlist?.enabled != false && (
                <WishList
                  wishItemsAmount={wishItemsAmount}
                  isLoggedIn={isLoggedIn}
                />
              )}
              {primaryHeaderConfig?.cart?.enabled != false && (
                <MiniCart cartItemsAmount={cartItemsAmount} />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

PrimaryHeader.defaultProps = {
  isLoggedIn: false,
  isCCMember: false,
  isFlybuysLinked: false,
  userName: "",
};
